import {isEmpty} from 'lodash';
import {useState, useMemo, useCallback, Fragment} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Accordion, AccordionDetails, AccordionTitle,
    ActionButton, Icon, InputFormik, MultiInputFormik
} from 'components';
import {ConfirmModal} from 'components/modals';
import {Group, Subgroup} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';

import {GroupInfoService} from './group-info.service';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.GROUPS.GROUP_INFO;

const GROUP_FIELD = {
    NAME: 'name',
    PUBLIC_ID: 'publicId',
    FILE_PREFIX: 'filePrefix',
    SUBGROUPS: 'subgroups'
};

const SUBGROUP_FIELD = {
    NAME: 'name',
    PUBLIC_ID: 'publicId',
};

type Props = {
    index: number;
    group: Group;
    name: string;
    values: Group[];
    defaultExpanded: boolean;
    onRemove: (index: number) => void;
};

export const GroupInfo: React.FC<Props> = ({group, name, values, index, defaultExpanded, onRemove}: Props) => {
    const [confirmModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
    const {t} = useTranslation();

    const subgroups = useMemo(() => group?.subgroups || [], [group]);

    const openConfirmModal = useCallback(() => setConfirmationModalOpen(true), []);
    const closeConfirmModal = useCallback(() => setConfirmationModalOpen(false), []);

    const onRemoveGroup = useCallback(() => {
        onRemove(index);
    }, [index, onRemove]);

    const onCloseConfirmModal = useCallback((confirm: boolean) => {
        if (confirm) {
            onRemoveGroup();
        }
        closeConfirmModal();
    }, [onRemoveGroup, closeConfirmModal]);

    const onRemoveGroupRequest = useCallback(() => {
        // if not saved in database
        if (!group.id) {
            onRemoveGroup();
            return;
        }
        openConfirmModal();
    }, [group, onRemoveGroup, openConfirmModal]);

    const isSubgroupFieldError = useCallback((subgroup: Subgroup, key: string): boolean => {
        const subgroupErrors = GroupInfoService.getSubgroupError(t, subgroups, subgroup, key);
        return !!subgroupErrors[key];
    }, [subgroups, t]);

    const isSubgroupValid = useCallback((subgroup: Subgroup) =>
        !isEmpty(subgroup?.name) && !isEmpty(subgroup?.publicId) &&
        !isSubgroupFieldError(subgroup, 'name') && !isSubgroupFieldError(subgroup, 'publicId'), [isSubgroupFieldError]);

    return (
        <Fragment>
            <Accordion className='tw-my-5' defaultExpanded={defaultExpanded}>
                <AccordionTitle>
                    <div className='tw-flex tw-ts-h5-bold tw-items-center tw-justify-between tw-text-break-word tw-py-1'>
                        {name || `${t(T_KEYS.GROUP_NUM_TITLE)} ${index + 1}`}
                        <ActionButton action='delete' className='tw-mx-2 tw-ts-base-bold -sm:tw-hidden' onClick={onRemoveGroupRequest}>
                            {t(T_KEYS.BUTTON.REMOVE_GROUP)}
                        </ActionButton>
                        <div className='tw-mx-2 sm:tw-hidden'>
                            <Icon
                                button
                                name='delete'
                                color='alert'
                                onClick={onRemoveGroupRequest}
                            />
                        </div>
                    </div>
                </AccordionTitle>
                <AccordionDetails>
                    <div className='tw-grid lg:tw-grid-cols-8 tw-gap-3 lg:tw-mr-10'>
                        <InputFormik
                            required
                            name={`${index}.${GROUP_FIELD.NAME}`}
                            label={t(T_KEYS.GROUP_NAME)}
                            className='tw-col-span-3'
                        />
                        <InputFormik
                            required
                            name={`${index}.${GROUP_FIELD.PUBLIC_ID}`}
                            label={t(T_KEYS.GROUP_PUBLIC_ID)}
                            className='tw-col-span-2 -lg:tw-col-span-3'
                        />
                        <InputFormik
                            name={`${index}.${GROUP_FIELD.FILE_PREFIX}`}
                            label={t(T_KEYS.GROUP_FILE_PREFIX)}
                            description={t(T_KEYS.GROUP_FILE_PREFIX_HELPER)}
                            className='tw-col-span-3'
                        />
                    </div>
                    <div className='tw-mt-8'>
                        <div className='tw-font-bold tw-mb-4'>{t(T_KEYS.SUBGROUPS.TITLE)}</div>
                        <div className='tw-grid tw-grid-cols-12 tw-p-4 tw-bg-layout -lg:tw-hidden'>
                            <div className='tw-col-span-6'>{t(T_KEYS.SUBGROUPS.SUBGROUP_NAME)}</div>
                            <div className='tw-col-span-5'>{t(T_KEYS.SUBGROUPS.SUBGROUP_PUBLIC_ID)}</div>
                            <div className='tw-col-span-1 tw-text-nowrap tw-text-right'>{t(T_KEYS.SUBGROUPS.ACTIONS)}</div>
                        </div>
                        <MultiInputFormik
                            name={`${index}.${GROUP_FIELD.SUBGROUPS}`}
                            itemsPath={`${GROUP_FIELD.SUBGROUPS}`}
                            defaultValue={{publicId: '', name: ''}}
                            values={values[index]}
                            isValid={isSubgroupValid}
                        >
                            {({index: controlIndex, item, path, last, renderActions}) => (
                                <div key={controlIndex} className='tw-flex tw-items-start tw-my-4 lg:tw-items-center'>
                                    <div className='tw-w-full tw-grid lg:tw-grid-cols-2 -lg:tw-grid-cols-1 tw-gap-3 -lg:tw-mb-2'>
                                        <InputFormik
                                            required={!last}
                                            validate={!last}
                                            name={`${index}.${path}.${SUBGROUP_FIELD.NAME}`}
                                            label={t(last ? T_KEYS.SUBGROUPS.NEW_SUBGROUP_NAME : T_KEYS.SUBGROUPS.SUBGROUP_NAME)}
                                        />
                                        <InputFormik
                                            required={!last}
                                            validate={!last}
                                            name={`${index}.${path}.${SUBGROUP_FIELD.PUBLIC_ID}`}
                                            label={t(last ? T_KEYS.SUBGROUPS.NEW_SUBGROUP_PUBLIC_ID : T_KEYS.SUBGROUPS.SUBGROUP_PUBLIC_ID)}
                                        />
                                    </div>
                                    <div className='tw-w-10 -lg:tw-mt-6'>{renderActions({index: controlIndex, item})}</div>
                                </div>
                            )}
                        </MultiInputFormik>
                    </div>
                </AccordionDetails>
            </Accordion>
            <ConfirmModal
                open={confirmModalOpen}
                title={t(T_KEYS.CONFIRM_REMOVE.TITLE)}
                content={t(T_KEYS.CONFIRM_REMOVE.CONTENT)}
                onClose={onCloseConfirmModal}
            />
        </Fragment>
    );
};
